import * as genericService from "./genericFormServices";
import type { PLOFormThreeData } from "../types/PLOs/plo_three.type";
import type { SubmissionStatus } from "../types/MainType.type";

const ENDPOINT = "plo-form-three";

export interface PLOFetchResponse {
  data: PLOFormThreeData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for PLO Form Three
// ---------------------------------------------------------------------------
export const mapPloFormThreeBackendToFrontend = (item: any): PLOFormThreeData => {
  if (!item) return {} as PLOFormThreeData;

  // Extract nested form entity from backend response structure (PLO Form Three)
  const form = item.pLOFormThree || item.ploFormThree || item.formData || item;

  return {
    id: item.id || form.id || '',
    documentNumber: item.documentNumber || '',
    
    // Core Form Three Fields
    officeId: form.officeId || '',
    caseYear: form.caseYear || '',
    caseNumber: form.caseNumber || '',
    caseType: form.caseType || '',
    offenseCategory: form.offenseCategory || '',
    lawSection: form.lawSection || '',
    adviceRequestedDate: form.adviceRequestedDate || '',
    turnedDownDate: form.turnedDownDate || '',
    adviceGivenDate: form.adviceGivenDate || '',
    prosecutedDate: form.prosecutedDate || '',
    courtCaseNumber: form.courtCaseNumber || '',
    plaintiffName: form.plaintiffName || '',
    defendantName: form.defendantName || '',
    defendantStatus: form.defendantStatus || '',
    remandStatus: form.remandStatus || '',
    trialPeriod: form.trialPeriod || '',
    residentArea: form.residentArea || '',

    // Status Metadata
    status: (item.status || form.status || 'DRAFT') as SubmissionStatus,

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // System Dates & Audit
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory) ? item.stageHistory : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Filter Function for PLO Form Three
// ---------------------------------------------------------------------------
const ploFilterPredicate = (
  app: PLOFormThreeData,
  params: FetchPloThreeParams
): boolean => {
  const {
    searchTerm,
    caseYear,
    partySearch,
    selectedStatus,
    adviceGivenDate,
    prosecutedStartDate,
    prosecutedEndDate,
  } = params;

  // Status Filter
  const matchesStatus = !selectedStatus || app.status === selectedStatus;

  // Case Year Filter (supports string or numeric comparisons)
  const matchesCaseYear =
    !caseYear ||
    Boolean(app.caseYear && String(app.caseYear).trim() === String(caseYear).trim());

  // Party Search Filter (Plaintiff or Defendant)
  const partyTerm = (partySearch || "").toLowerCase().trim();
  const matchesPartySearch =
    !partyTerm ||
    Boolean(app.plaintiffName && app.plaintiffName.toLowerCase().includes(partyTerm)) ||
    Boolean(app.defendantName && app.defendantName.toLowerCase().includes(partyTerm));

  // Advice Given Date Match
  const matchesAdviceGivenDate =
    !adviceGivenDate ||
    Boolean(app.adviceGivenDate && app.adviceGivenDate.startsWith(adviceGivenDate));

  // Prosecuted Date Range Match (Start & End)
  let matchesProsecutedDateRange = true;
  if (app.prosecutedDate) {
    const prosDate = app.prosecutedDate.split("T")[0]; // Format YYYY-MM-DD
    if (prosecutedStartDate && prosDate < prosecutedStartDate) {
      matchesProsecutedDateRange = false;
    }
    if (prosecutedEndDate && prosDate > prosecutedEndDate) {
      matchesProsecutedDateRange = false;
    }
  } else if (prosecutedStartDate || prosecutedEndDate) {
    matchesProsecutedDateRange = false;
  }

  // General Search Filter
  const term = (searchTerm || "").toLowerCase().trim();
  const matchesGeneralSearch =
    !term ||
    Boolean(app.caseNumber && app.caseNumber.toLowerCase().includes(term)) ||
    Boolean(app.courtCaseNumber && app.courtCaseNumber.toLowerCase().includes(term)) ||
    Boolean(app.plaintiffName && app.plaintiffName.toLowerCase().includes(term)) ||
    Boolean(app.defendantName && app.defendantName.toLowerCase().includes(term)) ||
    Boolean(app.lawSection && app.lawSection.toLowerCase().includes(term)) ||
    Boolean(app.offenseCategory && app.offenseCategory.toLowerCase().includes(term)) ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term));

  return Boolean(
    matchesStatus &&
      matchesCaseYear &&
      matchesPartySearch &&
      matchesAdviceGivenDate &&
      matchesProsecutedDateRange &&
      matchesGeneralSearch
  );
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

export interface FetchPloThreeParams {
  searchTerm?: string;
  caseYear?: string;
  partySearch?: string;
  selectedStatus?: string;
  adviceGivenDate?: string;
  prosecutedStartDate?: string;
  prosecutedEndDate?: string;
  currentPage: number;
  itemsPerPage: number;
}

// Fetch All / Filtered Applications
export const fetchPloApplications = (
  params: FetchPloThreeParams
): Promise<PLOFetchResponse> =>
  genericService.fetchApplications<PLOFormThreeData>(
    ENDPOINT,
    params,
    mapPloFormThreeBackendToFrontend,
    ploFilterPredicate
  );

// Fetch Single Application by ID
export const fetchPloById = (id: string): Promise<PLOFormThreeData> =>
  genericService.fetchApplicationById<PLOFormThreeData>(
    ENDPOINT,
    id,
    mapPloFormThreeBackendToFrontend
  );

// Save Draft (Single item or Array/Bulk payload)
export const savePloDraft = (
  id: string | null,
  data: Partial<PLOFormThreeData> | Partial<PLOFormThreeData>[]
) => genericService.saveDraft<PLOFormThreeData>(ENDPOINT, id, data as any);

// Delete Form (Soft Delete)
export const deletePloForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);